import React from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ConnectorStatus } from '@/types/connectors';
import { getPlatformMeta } from './platformLogos';

export interface ActiveFilterChipsProps {
  platforms: string[];
  statuses: ConnectorStatus[];
  onRemovePlatform: (platform: string) => void;
  onRemoveStatus: (status: ConnectorStatus) => void;
  onClearAll: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  connected: 'Connected',
  running: 'Running',
  error: 'Error',
  disabled: 'Disabled',
};

export const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
  platforms,
  statuses,
  onRemovePlatform,
  onRemoveStatus,
  onClearAll,
}) => {
  const count = platforms.length + statuses.length;
  if (count === 0) return null;

  const chip = (label: string, onRemove: () => void) => (
    <span
      key={label}
      className="inline-flex items-center gap-1.5 rounded-full border bg-muted pl-2.5 pr-1.5 py-0.5 text-xs"
    >
      {label}
      <button
        type="button"
        aria-label={`Remove ${label}`}
        onClick={onRemove}
        className="inline-flex rounded-full p-0.5 text-muted-foreground hover:bg-border hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {statuses.map((s) => chip(`Status: ${STATUS_LABEL[s] ?? s}`, () => onRemoveStatus(s)))}
      {platforms.map((p) => chip(`Platform: ${getPlatformMeta(p).displayName}`, () => onRemovePlatform(p)))}
      {count > 1 && (
        <Button variant="ghost" size="sm" onClick={onClearAll}>
          Clear all
        </Button>
      )}
    </div>
  );
};
